import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import BasketItemResource from "@/resources/BasketItemResource"
import Distributions from "@/config/Distributions"
import { useBasketHttp } from "@/http/basketHttp"
import { useSessionStore } from "@/stores/useSessionStore"
import { BasketItemProductResource } from "@/resources/cart/BasketItemProductResource"
import AttributeTypes from "@/config/AttributeTypes"
import { useModalStore } from "@/stores/useModalStore"
import { format, isAfter, parse } from "date-fns"
import { nl } from "date-fns/locale"
import { useEstablishmentHttp } from "@/http/establishmentHttp"
import { useModalsStore } from "@/stores/useModalsStore"
import { CART_CHANGED_MODAL } from "@/types/Modals"
import { useI18n } from "vue-i18n"
import type { EstablishmentDistribution } from "@/types/http/EstablishmentDistribution"
import { Distribution } from '@/enums/Distribution'
import type { Establishment } from "@/types/http/Establishment"
import type { BasketInfo } from "@/types/http/BasketInfo"

export type Slot = {
  available: boolean
  available_slots: number
  full: boolean
  label: string // 13:15 - 13:30
  time: string // 13:15
  timeslot: string // 13:15 - 13:30
}

export type Timeslot = {
  available: boolean
  date: string // 13-03-2024
  label: string // Woensdag 13 maart (vandaag)
  slots: Slot[]
  slots_available_count: number
  slots_count: number
}

export const useCartStore = defineStore('cart', () => {
  const sessionStore = useSessionStore()
  const modalStore = useModalStore()
  const { t } = useI18n()

  const _basketInfo = ref<BasketInfo | null>(null)
  const hasBasketInfo = computed<boolean>(() => _basketInfo.value !== null)
  const needsPayment = computed<boolean>(() => _basketInfo.value !== null ? _basketInfo.value.costs.requires_payment : false)

  const _basketItems = ref<BasketItemResource[]>([])
  const hasBasketItems = computed<boolean>(() => _basketItems.value.length > 0)

  const _distribution = ref<Distribution | null>(null)
  const setDistribution = (distributionValue): void => _distribution.value = distributionValue

  const _establishment = ref<Establishment | null>(null)
  const setEstablishment = (value: Establishment) => _establishment.value = value

  const _establishmentDistribution = ref<EstablishmentDistribution | null>(null)
  const setEstablishmentDistribution = (value: EstablishmentDistribution) => _establishmentDistribution.value = value

  const hasCart = computed<boolean>(() => _basketItems.value.length > 0 && _establishment.value !== null)

  const _isSyncing = ref<boolean>(false)
  const setSyncing = (value: boolean) => _isSyncing.value = value

  const add = async ({ product, productSetId, selectedAttributes, amount, distribution = null }) => {
    setSyncing(true)

    // Add the item to the basket
    addToBasketItems({
      product,
      productSetId,
      selectedAttributes,
      amount,
    })

    if (distribution !== null) {
      _distribution.value = distribution
    }

    // because we are adding a product, we need to set the cart establishment
    // the cart establishment will be the current establishment
    // so later on, we can check if the establishment is the same as the cart establishment
    // await dispatch('setEstablishment', rootState.establishment.establishment) // @todo: move upwards?

    // const ga = useGa({
    //   gtm: this.$gtm,
    //   store: this.$store,
    //   sentry: this.$sentry,
    // })
    //
    // ga.trackAddProduct({
    //   product,
    //   amount,
    //   selectedAttributes,
    //   establishment: getters.establishment,
    // })

    // Fetch new basket info from the server
    await fetchBasketInfo() // Hmmmmmmmmmmm @todo: network call in store

    setSyncing(false)
  }

  const addToBasketItems = ({ product, productSetId, selectedAttributes, amount }) => {
    const basketItemResource = new BasketItemResource({
      product,
      productSetId,
      selectedAttributes,
      amount,
    })

    const index = _basketItems.value.findIndex(basketItem => basketItem.product.hash === basketItemResource.product.hash)

    if (index === -1) {
      // in this case: product doesn't exist yet
      _basketItems.value.push(basketItemResource)
    } else {
      // product exists, increment it with $amount
      _basketItems.value[index].amount += basketItemResource.amount
    }
  }

  const fetchBasketInfo = async () => {
    if (_establishment.value === null || !hasBasketItems.value) {
      // Reset basket to original state
      clearBasketItems()

      return
    }

    let data = {
      establishment: {
        id: _establishment.value.id,
      },
      distribution: _distribution.value,
      restaurant_tip: tip.value,
    }

    if (couponCode.value !== null) {
      data.coupon = {
        code: couponCode.value,
      }
    }

    if (giftcardCode.value !== null) {
      data.giftcard = {
        code: giftcardCode.value,
      }
    }

    if (_distribution.value === Distributions.Delivery) {
      data.location = {
        lat: sessionStore.location.coordinates.lat,
        lng: sessionStore.location.coordinates.lng,
      }
    }

    data.products = _basketItems.value.map((productResource) => ({
      id: productResource.product.id,
      product_set_id: productResource.product.product_set_id,
      amount: productResource.amount,
      instructions: productResource.product.instructions,
      sub_product_groups: productResource.product.sub_product_groups.map((subProductGroup) => ({
        id: subProductGroup.id,
        sub_products: subProductGroup.sub_products.map((subProduct) => ({
          id: subProduct.id,
          amount: subProduct.amount,
        })),
      })),
    }))

    // validate the basket using the API
    try {
      const { validate } = useBasketHttp()

      const basketInfo = await validate(data)

      updateCartByServer(basketInfo)

      showModalByServer(basketInfo.modal)
    } catch (error) {

      if (error.response.data.data?.code === 'UNKNOWN_PRODUCTS') {
        clearBasketItems()

        return
      }

      if (error.response.data.data?.code === 'INVALID_TIMESLOT') {
        const { message } = error.response.data.data

        // Show the message from the server (timeslot not available anymore)
        // and in the callback, make sure we reload timeslots and show the checkout details modal
        modalStore.show({
          dataTestId: 'invalid-timeslot-modal',
          message: message,
        })

        return
      }

      return Promise.reject(error)
    }
  }

  const updateCartByServer = (basketInfo) => {
    clearBasketItems()

    _basketInfo.value = basketInfo

    basketInfo.products.forEach((item) => {
      const product = new BasketItemProductResource(item)

      const selectedAttributes = new Map()
      item.sub_product_groups.map((subProductGroup) => {
        if (subProductGroup.sub_products.length === 0) {
          return
        }

        if (subProductGroup.type === AttributeTypes.DropDown || subProductGroup.type === AttributeTypes.OptionalDropdown) {
          selectedAttributes.set(
            subProductGroup.id,
            String(subProductGroup.sub_products[0].id),
          )

          return
        }

        // checkbox
        selectedAttributes.set(
          subProductGroup.id,
          subProductGroup.sub_products.reduce((prev, current) => {
            return {
              ...prev,
              [current.id]: true,
            }
          }, {}),
        )
      })

      addToBasketItems({
        product,
        productSetId: product.product_set_id,
        selectedAttributes: Array.from(selectedAttributes),
        amount: item.amount,
      })
    })
  }

  const changeAmount = async (hash, amount) => {
    setSyncing(true)

    const index = _basketItems.value.findIndex(item => item.product.hash === hash)

    // If the amount == 0, assume that we need to remove the basket item
    if (amount === 0) {
      _basketItems.value.splice(index, 1)
    } else {
      _basketItems.value[index].amount = amount
    }

    await fetchBasketInfo()

    setSyncing(false)
  }

  const clearBasketItems = () => {
    _basketInfo.value = null
    _basketItems.value = []
  }

  const _distributionDate = ref<string | null>(null)
  const setDistributionDate = (date: any) => _distributionDate.value = date
  const _distributionTime = ref<string | null>(null)
  const setDistributionTime = (time: any) => _distributionTime.value = time

  const laterDistributionDateDescription = computed<string | null>(() => {
    if (!isAfter(parse(_distributionDate.value, 'dd-MM-yyyy', new Date()), new Date())) {
      return null
    }

    let date = parse(_distributionDate.value, 'dd-MM-yyyy', new Date())

    return format(date, 'd MMMM', { locale: nl })
  })

  const fetchTimeslots = async () => {
    const products = _basketInfo.value.products.map((product) => ({
      product_id: product.id,
      product_set_id: product.product_set_id,
      quantity: product.amount,
    }))

    const params = {
      distribution: _distribution.value,
      products,
    }

    if (_distribution.value === Distributions.Delivery) {
      const {
        coordinates: {
          lat,
          lng,
        },
      } = sessionStore.location

      params.location = {
        lat,
        lng,
      }
    }

    try {
      const { timeslots: timeslotsHttp } = useEstablishmentHttp()

      timeslots.value = await timeslotsHttp(
        _establishment.value.slug,
        _establishment.value.location.city.country.code.toLowerCase(),
        _establishment.value.location.city.slug,
        params,
      )
    } catch (e) {
      timeslots.value = []
    } finally {
      await selectFirstAvailableDateSlot()
    }
  }

  const selectFirstAvailableDateSlot = () => {
    if (timeslots.value.length === 0) {
      return
    }

    const availableDates = timeslots.value.filter((dateSlot) => dateSlot.available)

    // No available dates, no selection.
    if (availableDates.length === 0) {
      return
    }

    if (_distributionDate.value) {
      const selectedAvailableDate = availableDates.find((dateSlot) => dateSlot.date === _distributionDate.value)

      if (selectedAvailableDate) {
        // Picked date exists, only verify that timeslot is available.
        const selectedAvailableTimeslot = selectedAvailableDate.slots
          .filter((timeSlot) => timeSlot.available)
          .find((timeSlot) => timeSlot.time === _distributionTime.value)

        // timeslot is available
        if (selectedAvailableTimeslot) {
          return
        }

        _distributionTime.value = selectedAvailableDate.slots.find((timeSlot) => timeSlot.available).time
      }
    }

    _distributionDate.value = availableDates[0].date
    _distributionTime.value = availableDates[0].slots.find((timeSlot) => timeSlot.available).time
  }

  const timeslots = ref<Timeslot[]>([])

  const tip = ref<number>(0.0)
  const setTip = (value: number) => tip.value = value

  const giftcardCode = ref<string | null>(null)
  const setGiftcardCode = (value: string | null) => giftcardCode.value = value

  const couponCode = ref<string | null>(null)
  const setCouponCode = (value: string | null) => couponCode.value = value

  const setBasketItemInstructions = async (hash, instructions) => {
    _isSyncing.value = true

    try {
      const index = _basketItems.value.findIndex(item => item.product.hash === hash)

      _basketItems.value[index].product.instructions = instructions

      await fetchBasketInfo()
    } finally {
      _isSyncing.value = false
    }
  }

  const showModalByServer = (modal) => {
    // Check if we must show a modal, show the modal if so
    if (!modal) {
      return
    }

    modalStore.show({
      title: modal.title,
      message: modal.text,
    })
  }

  const updateCartByDistribution = async (toDistribution: Distribution, fromDistribution: Distribution) => {
    const data = {
      establishment: {
        id: _establishment.value.id,
      },
      distribution: fromDistribution,
      restaurant_tip: tip.value,
    }

    if (couponCode.value !== null) {
      data.coupon = {
        code: couponCode.value,
      }
    }

    if (giftcardCode.value !== null) {
      data.giftcard = {
        code: giftcardCode.value,
      }
    }

    if (_distribution.value === Distributions.Delivery && sessionStore.location !== null) {
      data.location = {
        lat: sessionStore.location.coordinates.lat,
        lng: sessionStore.location.coordinates.lng,
      }
    }

    data.products = _basketItems.value.map((productResource) => ({
      id: productResource.product.id,
      product_set_id: productResource.product.product_set_id,
      amount: productResource.amount,
      instructions: productResource.product.instructions,
      sub_product_groups: productResource.product.sub_product_groups.map((subProductGroup) => ({
        id: subProductGroup.id,
        sub_products: subProductGroup.sub_products.map((subProduct) => ({
          id: subProduct.id,
          amount: subProduct.amount,
        })),
      })),
    }))

    data.new_distribution = toDistribution

    let error = false
    const { changeDistributionType } = useBasketHttp()

    const { toggle } = useModalsStore()

    const response = await changeDistributionType(data).catch((e) => {
      if (e.response.status === 400) {
        error = true

        clearBasketItems()
      }

      if (e.response.status === 422) {
        error = true

        clearBasketItems()
      }
    })

    if (error) {
      return
    }

    if (response.removed.length > 0) {
      toggle(CART_CHANGED_MODAL, true, {
        ...response,
        distribution: toDistribution,
        old_distribution: fromDistribution,
      })

      return
    }

    await applyCartChanges({
      ...response,
      new_distribution: Number(toDistribution),
    })

    sessionStore.setDistribution(toDistribution)
    setDistribution(toDistribution)
    await fetchBasketInfo()
  }

  const applyCartChanges = async (changes) => {
    for (const remove of changes.removed) {
      await changeAmount(remove.hash, 0)
    }

    for (const change of changes.changes) {
      changeProductSetOfBasketItem(change.old.hash, change.new.product_set_id)
    }

    // Check if overlapping products with time limitations result in 0 timeslots
    if (changes.changes.length > 0) {
      let basketItems = _basketItems.value.map((item) => {
        return {
          product_id: item.product.id,
          product_set_id: item.product.product_set_id,
          quantity: item.amount,
        }
      })

      const params = {
        distribution: changes.new_distribution,
        products: basketItems,
      }

      if (params.distribution === Distributions.Delivery) {
        params.location = sessionStore.location.coordinates
      }

      const { timeslots } = useEstablishmentHttp()

      const timeSlots = await timeslots(
        _establishment.value.slug,
        _establishment.value.location.city.country.code.toLowerCase(),
        _establishment.value.location.city.slug,
        params,
      )

      const slotsAvailableTotal = timeSlots.reduce((acc, curr) => acc + curr.slots_available_count, 0)

      if (slotsAvailableTotal === 0) {
        modalStore.show({
          closable: true,
          dataTestId: 'empty-cart-confirmation-modal',
          title: t('confirmation-modal.notimeslots-distribution.title', {
            new_type: changes.new_distribution === Distributions.Delivery
              ? t('terms.delivery')
              : t('distribution-toggle.takeaway'),
          }),
          message: t('confirmation-modal.notimeslots-distribution.message', {
            new_type: changes.new_distribution === Distributions.Delivery
              ? t('terms.delivery').toLowerCase()
              : t('distribution-toggle.takeaway').toLowerCase(),
            old_type: _distribution.value === Distributions.Delivery
              ? t('terms.delivery').toLowerCase()
              : t('distribution-toggle.takeaway').toLowerCase(),
          }),
          callbacks: [
            {
              label: t('confirmation-modal.notimeslots-distribution.cancel-btn'),
              dataTestId: 'notimeslots-distribution-confirmation-modal-cancel',
              action: () => {
                fetchTimeslots()
              },
              properties: {
                type: 'secondary',
              },
            },
            {
              label: t('confirmation-modal.notimeslots-distribution.confirm-btn', {
                old_type: _distribution.value === Distributions.Delivery ? t('terms.delivery')
                  .toLowerCase() : t('distribution-toggle.takeaway').toLowerCase(),
              }),
              dataTestId: 'notimeslots-distribution-confirmation-modal-confirm',
              action: () => {
                clearBasketItems()
              },
            },
          ],
        })
      }
    }
  }

  const changeProductSetOfBasketItem = (hash, productSetId) => {
    const index = _basketItems.value.findIndex(item => item.product.hash === hash)

    if (index !== -1) {
      _basketItems.value[index].product.product_set_id = productSetId
    }
  }

  return {
    setDistribution,
    setEstablishment,
    hasCart,
    hasBasketItems,
    hasBasketInfo,
    setSyncing,

    add,
    changeAmount,
    clearBasketItems,

    fetchBasketInfo,

    setDistributionDate,
    setDistributionTime,
    laterDistributionDateDescription,

    tip,
    setTip,

    needsPayment,
    fetchTimeslots,
    timeslots,
    distributionDate: _distributionDate,
    distributionTime: _distributionTime,

    // persisted
    basketInfo: _basketInfo,
    basketItems: _basketItems,
    distribution: _distribution,
    establishment: _establishment,
    isSyncing: _isSyncing,

    giftcardCode,
    setGiftcardCode,

    couponCode,
    setCouponCode,

    setBasketItemInstructions,

    showModalByServer,

    updateCartByDistribution,
    applyCartChanges,
    updateCartByServer,

    selectFirstAvailableDateSlot,

    establishmentDistribution: _establishmentDistribution,
    setEstablishmentDistribution,
  }
}, { persist: true })